import { useId } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { inquiryApi, InvalidApiResponseError } from "../api";
import type { InquiryListParams } from "../api/interfaces";

/** Mount-local pages avoid replaying an accumulated private inbox on remount. */
export function useInquiryInbox(
  actorId: string | undefined,
  params: InquiryListParams,
) {
  const instance = useId();
  return useInfiniteQuery({
    queryKey: ["social", "inquiries", actorId, params, instance],
    enabled:
      Boolean(actorId) &&
      (params.scope !== "facility" || Boolean(params.facility_id)),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const page = await inquiryApi.list(
        { ...params, ...(pageParam ? { cursor: pageParam } : {}) },
        signal,
      );
      if (
        page.has_more &&
        (!page.next_cursor || page.next_cursor === pageParam)
      )
        throw new InvalidApiResponseError("/social/inquiries");
      return page;
    },
    getNextPageParam: (page) =>
      page.has_more ? (page.next_cursor ?? undefined) : undefined,
    retry: false,
    gcTime: 0,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}
